import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  v2AdminAddFeatureFlagInvestorMutation,
  v2AdminAddFeatureFlagStartupMutation,
  v2AdminListFeatureFlagsOptions,
  v2AdminListFeatureFlagsQueryKey,
  v2AdminRemoveFeatureFlagInvestorMutation,
  v2AdminRemoveFeatureFlagStartupMutation,
  v2AdminSetFeatureFlagEnabledMutation,
} from '@workspace/api-backend/react-query'

export function useAdminFeatureFlagsQuery() {
  return useQuery(v2AdminListFeatureFlagsOptions())
}

/**
 * Toggle the global enabled state of a feature flag. Invalidates the list
 * query AND every per-startup feature-flag query (key prefix
 * `'v2GetStartupFeatureFlags'`) so founder views update in real time.
 */
export function useSetFeatureFlagEnabledMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminSetFeatureFlagEnabledMutation(),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: v2AdminListFeatureFlagsQueryKey() }),
        queryClient.invalidateQueries({ queryKey: ['v2GetStartupFeatureFlags'] }),
      ])
    },
  })
}

export function useAddFeatureFlagStartupMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminAddFeatureFlagStartupMutation(),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: v2AdminListFeatureFlagsQueryKey() }),
        queryClient.invalidateQueries({ queryKey: ['v2GetStartupFeatureFlags'] }),
      ])
    },
  })
}

export function useRemoveFeatureFlagStartupMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminRemoveFeatureFlagStartupMutation(),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: v2AdminListFeatureFlagsQueryKey() }),
        queryClient.invalidateQueries({ queryKey: ['v2GetStartupFeatureFlags'] }),
      ])
    },
  })
}

/** Investor allowlists decide `assistantEnabled`, so those views refresh with the list. */
function useRefreshAfterInvestorChange() {
  const queryClient = useQueryClient()
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: v2AdminListFeatureFlagsQueryKey() }),
      queryClient.invalidateQueries({
        predicate: (query) =>
          (query.queryKey[0] as { _id?: string } | undefined)?._id === 'v2InvestorGetMcpSettings',
      }),
    ])
  }
}

export function useAddFeatureFlagInvestorMutation() {
  const refresh = useRefreshAfterInvestorChange()
  return useMutation({ ...v2AdminAddFeatureFlagInvestorMutation(), onSuccess: refresh })
}

export function useRemoveFeatureFlagInvestorMutation() {
  const refresh = useRefreshAfterInvestorChange()
  return useMutation({ ...v2AdminRemoveFeatureFlagInvestorMutation(), onSuccess: refresh })
}
